// src/components/NezoSso.js
//
// Landing page for "Create material" in Nezo (and "Continue with Nezo" on the login page).
// The Nezo gateway sends the user here as /sso?code=… — a 60-second, single-use code. The backend
// (routes/sso.js) verifies it and returns a custom token for this Firebase project, so a Nezo user is
// signed straight in with no separate Ops Console password.

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { getAuth, signInWithCustomToken, signOut } from 'firebase/auth';
import { API_BASE_URL, setRegion } from '../services/api';
import { getNezoAppUrl, setNezoAppUrl } from '../utils/nezoApp';


// A code can be redeemed once. React StrictMode runs effects twice in development, so remember the
// in-flight redeem per code rather than per mount.
const redeemsInFlight = new Map();

function redeemOnce(code) {
  if (!redeemsInFlight.has(code)) {
    redeemsInFlight.set(code, fetch(`${API_BASE_URL}/sso/redeem`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ code }),
    }).then(async (res) => ({ ok: res.ok, status: res.status, body: await res.json().catch(() => ({})) })));
  }
  return redeemsInFlight.get(code);
}

const styles = {
  page: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(160deg, #3d9a8b 0%, #7cc7a9 100%)',
    padding: '16px',
    boxSizing: 'border-box',
  },
  card: {
    width: '100%',
    maxWidth: 440,
    background: '#ffffff',
    borderRadius: 16,
    padding: '32px 28px',
    boxShadow: '0 12px 40px rgba(0,0,0,0.12)',
    textAlign: 'center',
    fontFamily: 'inherit',
  },
  logo: { height: 40, width: 'auto', marginBottom: 20 },
  title: { margin: '0 0 8px', fontSize: '1.3rem', color: '#1f2a37' },
  text: { margin: '0 0 20px', color: '#4b5563', lineHeight: 1.5 },
  spinner: {
    width: 28,
    height: 28,
    margin: '4px auto 0',
    border: '3px solid #d1fae5',
    borderTopColor: '#3d7a72',
    borderRadius: '50%',
    animation: 'nezo-sso-spin 0.8s linear infinite',
  },
  button: {
    display: 'inline-block',
    padding: '0.75rem 1.4rem',
    borderRadius: 10,
    background: '#3d7a72',
    color: '#ffffff',
    fontWeight: 600,
    textDecoration: 'none',
  },
  link: { display: 'block', marginTop: 14, color: '#3d7a72', fontSize: '0.9rem' },
};

export default function NezoSso() {
  const location = useLocation();
  const [state, setState] = useState({ phase: 'working', message: '', email: '' });
  const started = useRef(false);

  useEffect(() => {
    if (started.current) return;
    started.current = true;

    const code = new URLSearchParams(location.search).get('code');
    // Keep the code out of history and out of anything that reads the address bar later.
    window.history.replaceState(null, '', '/sso');

    if (!code) {
      setState({ phase: 'error', message: 'This sign-in link is missing its code. Please go back to Nezo and try again.' });
      return;
    }

    (async () => {
      try {
        const { ok, body } = await redeemOnce(code);
        if (!ok) {
          setState({
            phase: 'error',
            message: body.message || 'We could not sign you in. Please go back to Nezo and try again.',
            isAdmin: body.error === 'admin',
          });
          return;
        }

        const auth = getAuth();
        const current = auth.currentUser;
        const sameUser = current && (current.email || '').toLowerCase() === body.email;
        if (!sameUser) {
          if (current) await signOut(auth);
          await signInWithCustomToken(auth, body.customToken);
        }

        if (body.region) setRegion(body.region);
        // remember which Nezo (staging or prod) to send Back / Go to Nezo to
        if (body.app) setNezoAppUrl(body.app);
        setState({ phase: 'done', message: '', email: body.email });

        // A full page load (not a client-side navigate) so the region, role and layout all start
        // fresh for this user, exactly as after a normal sign-in.
        setTimeout(() => window.location.replace(body.returnTo || '/composite-requests'), 700);
      } catch (err) {
        console.error('❌ Nezo sign-on failed:', err);
        setState({ phase: 'error', message: 'We could not sign you in. Please go back to Nezo and try again.' });
      }
    })();
  }, [location.search]);

  return (
    <div style={styles.page}>
      <style>{'@keyframes nezo-sso-spin { to { transform: rotate(360deg); } }'}</style>
      <div style={styles.card}>
        <img src="/icons/Nezo_Navy_Fabricon.png" alt="Nezo" style={styles.logo} />

        {state.phase === 'working' && (
          <>
            <h1 style={styles.title}>Signing you in from Nezo…</h1>
            <p style={styles.text}>This only takes a moment.</p>
            <div style={styles.spinner} />
          </>
        )}

        {state.phase === 'done' && (
          <>
            <h1 style={styles.title}>You're signed in</h1>
            <p style={styles.text}>
              Signed in as <strong>{state.email}</strong>. Opening the Ops Console…
            </p>
            <div style={styles.spinner} />
          </>
        )}

        {state.phase === 'error' && (
          <>
            <h1 style={styles.title}>We couldn't sign you in</h1>
            <p style={styles.text}>{state.message}</p>
            {state.isAdmin ? (
              <a href="/login" style={styles.button}>Sign in with password</a>
            ) : (
              <a href={getNezoAppUrl()} style={styles.button}>Back to Nezo</a>
            )}
            <a href="/login" style={styles.link}>Sign in to the Ops Console another way</a>
          </>
        )}
      </div>
    </div>
  );
}
